<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import DimensionPicker from './DimensionPicker.vue';

const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    dimensions: {
        type: Object,
        default: () => ({}),
    },
    selectedDimensions: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['update:selectedDimensions']);

const selectedDimensions = ref([...props.selectedDimensions]);

watch(() => props.selectedDimensions, v => {
    if (JSON.stringify(v) !== JSON.stringify(selectedDimensions.value)) {
        selectedDimensions.value = [...v];
    }
}, { deep: true });
watch(selectedDimensions, v => {
    if (JSON.stringify(v) !== JSON.stringify(props.selectedDimensions)) {
        emit('update:selectedDimensions', [...v]);
    }
}, { deep: true });
const beginAtZero = ref(true);
const canvasRef = ref(null);
let chart = null;

const showChart = computed(() => props.data.length > 0 && selectedDimensions.value.length > 0);

const colors = [
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(255, 99, 132, 0.8)',
];

function collectValues(texts, dimensionKey) {
    if (!texts || !texts.length) return [];
    return texts.map(t => Number(t.values[dimensionKey]) || 0);
}

function buildChartData() {
    const groupLabels = props.data.map(group => group.name);

    const datasets = selectedDimensions.value.map((dimKey, i) => ({
        label: props.dimensions[dimKey] || dimKey,
        data: props.data.map(group => collectValues(group.texts, dimKey)),
        backgroundColor: colors[i % colors.length].replace('0.8', '0.5'),
        borderColor: colors[i % colors.length],
        borderWidth: 1,
        outlierColor: colors[i % colors.length],
        itemRadius: 2,
        itemBackgroundColor: colors[i % colors.length],
    }));

    return { labels: groupLabels, datasets };
}

function buildOptions() {
    return {
        plugins: {
            legend: { display: true, position: 'bottom' },
            zoom: {
                pan: { enabled: true, mode: 'y' },
                zoom: {
                    wheel: { enabled: true },
                    pinch: { enabled: true },
                    mode: 'y',
                },
                limits: {
                    y: { min: 'original', max: 'original' },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: beginAtZero.value,
            },
        },
    };
}

function destroyChart() {
    if (chart) {
        chart.destroy();
        chart = null;
    }
}

function render() {
    if (!showChart.value || !canvasRef.value) return;

    const data = buildChartData();
    const options = buildOptions();

    if (chart) {
        chart.data = data;
        chart.options = options;
        chart.update();
    } else {
        const Chart = window.chartJS.Chart;
        chart = new Chart(canvasRef.value.getContext('2d'), {
            type: 'boxplot',
            data,
            options,
        });
    }
}

watch(showChart, async (visible) => {
    if (!visible) {
        destroyChart();
    } else {
        await nextTick();
        render();
    }
});

watch([() => props.data, selectedDimensions, beginAtZero], async () => {
    if (!showChart.value) {
        destroyChart();
        return;
    }
    await nextTick();
    render();
}, { deep: true });

onMounted(async () => {
    await nextTick();
    if (showChart.value) render();
});

onBeforeUnmount(() => {
    destroyChart();
});
</script>

<template>
    <div>
        <canvas v-if="showChart" ref="canvasRef" width="800" height="400"></canvas>
        <p v-else class="text-muted border rounded p-3 mb-0 mt-2">
            <span v-if="!data.length">Keine Daten geladen. Wählen Sie Datensätze und klicken Sie auf „Daten laden“.</span>
            <span v-else>Bitte mindestens eine Dimension auswählen.</span>
        </p>
        <div v-if="showChart" class="form-check mt-4">
            <input class="form-check-input" type="checkbox" id="boxplot-begin-at-zero" v-model="beginAtZero" />
            <label class="form-check-label" for="boxplot-begin-at-zero">Y-Achse bei 0 beginnen</label>
        </div>
        <DimensionPicker class="mt-3" :dimensions="dimensions" v-model="selectedDimensions" id-prefix="boxplot" />
    </div>
</template>
